import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Plus, ShoppingCart, FileText, Truck, Users, TrendingUp } from 'lucide-react';

export default function Purchasing() {
  const [tab, setTab] = useState('requisitions');
  const tabs = [
    { key: 'requisitions', label: 'Requisitions', icon: FileText },
    { key: 'purchase-orders', label: 'Purchase Orders', icon: ShoppingCart },
    { key: 'suppliers', label: 'Supplier Master', icon: Users },
    { key: 'goods-receipts', label: 'Goods Receipts', icon: Truck },
    { key: 'tracker', label: 'Req Status Tracker', icon: TrendingUp },
  ];

  return (
    <div>
      <div className="tabs">
        {tabs.map(t => {
          const Icon = t.icon;
          return <div key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}><Icon size={14} />{t.label}</div>;
        })}
      </div>
      {tab === 'requisitions' && <RequisitionsTab />}
      {tab === 'purchase-orders' && <PurchaseOrdersTab />}
      {tab === 'suppliers' && <SuppliersTab />}
      {tab === 'goods-receipts' && <GoodsReceiptsTab />}
      {tab === 'tracker' && <TrackerTab />}
    </div>
  );
}

// Requisitions
function RequisitionsTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({});
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { loadData(); }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try { const res = await api.get('/purchasing/requisitions', { params: filter }); setData(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    try { await api.put(`/purchasing/requisitions/${id}`, { status: 'Approved', approved_by: 'supt_kim' }); loadData(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  return (
    <div>
      <div className="action-bar">
        <div style={{ display: 'flex', gap: 6 }}>
          {['', 'Draft', 'Submitted', 'Approved', 'PO Raised', 'Rejected'].map(s => (
            <button key={s} className={`btn btn-sm ${filter.status === s ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilter(s ? { status: s } : {})}>{s || 'All'}</button>
          ))}
        </div>
        <button className="btn btn-accent" onClick={() => setShowCreate(true)}><Plus size={15} /> New Requisition</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         data.length === 0 ? <div className="empty-state"><FileText /><h4>No requisitions</h4></div> :
         <div className="table-container">
           <table>
             <thead><tr><th>Req #</th><th>Type</th><th>Parts Summary</th><th>Required By</th><th>Status</th><th>Raised By</th><th>Date</th><th>Actions</th></tr></thead>
             <tbody>
               {data.map(r => (
                 <tr key={r.id}>
                   <td><strong>{r.requisition_number}</strong></td>
                   <td><span className={`badge ${r.requisition_type === 'Emergency' ? 'badge-emergency' : r.requisition_type === 'Urgent' ? 'badge-urgent' : r.requisition_type === 'AI-Generated' ? 'badge-ai' : 'badge-draft'}`}>{r.requisition_type}</span></td>
                   <td style={{ fontSize: 12 }}>{r.parts_list?.length || 0} item(s)</td>
                   <td>{r.required_by_date}</td>
                   <td><span className={`badge badge-${r.status.toLowerCase()}`}>{r.status}</span></td>
                   <td>{r.raised_by_name}</td>
                   <td>{r.date_raised}</td>
                   <td>
                     {r.status === 'Submitted' && <button className="btn btn-success btn-sm" onClick={() => handleApprove(r.id)}>Approve</button>}
                     {r.status === 'Draft' && <button className="btn btn-primary btn-sm" onClick={() => api.put(`/purchasing/requisitions/${r.id}`, { status: 'Submitted' }).then(loadData)}>Submit</button>}
                   </td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && <CreateReqModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadData(); }} />}
    </div>
  );
}

function CreateReqModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ requisition_type: 'Routine', required_by_date: '', remarks: '', parts: [{ part_number: '', qty: 1 }] });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/purchasing/requisitions', {
        requisition_type: form.requisition_type,
        required_by_date: form.required_by_date,
        remarks: form.remarks,
        parts_list: form.parts,
      });
      onCreated();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  const addPart = () => setForm({ ...form, parts: [...form.parts, { part_number: '', qty: 1 }] });
  const updatePart = (i, field, val) => { const parts = [...form.parts]; parts[i] = { ...parts[i], [field]: val }; setForm({ ...form, parts }); };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Create Requisition</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group"><label className="form-label">Type</label>
                <select className="form-select" value={form.requisition_type} onChange={e => setForm({ ...form, requisition_type: e.target.value })}>
                  {['Routine','Urgent','Emergency','AI-Generated'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Required By Date</label><input className="form-input" type="date" value={form.required_by_date} onChange={e => setForm({ ...form, required_by_date: e.target.value })} /></div>
            </div>
            <div className="form-group"><label className="form-label">Parts</label>
              {form.parts.map((p, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input className="form-input" placeholder="Part number" value={p.part_number} onChange={e => updatePart(i, 'part_number', e.target.value)} style={{ flex: 2 }} />
                  <input className="form-input" type="number" placeholder="Qty" value={p.qty} onChange={e => updatePart(i, 'qty', parseInt(e.target.value))} style={{ flex: 1 }} />
                </div>
              ))}
              <button type="button" className="btn btn-outline btn-sm" onClick={addPart}>+ Add Part</button>
            </div>
            <div className="form-group"><label className="form-label">Remarks</label><textarea className="form-textarea" value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create as Draft'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Purchase Orders
function PurchaseOrdersTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({});
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { loadData(); }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try { const res = await api.get('/purchasing/purchase-orders', { params: filter }); setData(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const statusBadge = (status) => {
    const map = { 'Draft': 'badge-draft', 'Sent': 'badge-info', 'Acknowledged': 'badge-healthy', 'Part-Delivered': 'badge-warning', 'Delivered': 'badge-closed', 'Closed': 'badge-closed', 'Cancelled': 'badge-rejected' };
    return <span className={`badge ${map[status] || 'badge-draft'}`}>{status}</span>;
  };

  return (
    <div>
      <div className="action-bar">
        <div style={{ display: 'flex', gap: 6 }}>
          {['', 'Draft', 'Sent', 'Acknowledged', 'Delivered'].map(s => (
            <button key={s} className={`btn btn-sm ${filter.status === s ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilter(s ? { status: s } : {})}>{s || 'All'}</button>
          ))}
        </div>
        <button className="btn btn-accent" onClick={() => setShowCreate(true)}><Plus size={15} /> Create PO</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         data.length === 0 ? <div className="empty-state"><ShoppingCart /><h4>No purchase orders</h4></div> :
         <div className="table-container">
           <table>
             <thead><tr><th>PO #</th><th>Supplier</th><th>Delivery Port</th><th>Required By</th><th>Total Value</th><th>Currency</th><th>Status</th><th>Req #</th></tr></thead>
             <tbody>
               {data.map(po => (
                 <tr key={po.id}>
                   <td><strong>{po.po_number}</strong></td>
                   <td>{po.supplier_name} <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>({po.country})</span></td>
                   <td>{po.delivery_port}</td>
                   <td>{po.required_by_date}</td>
                   <td style={{ fontWeight: 600 }}>{po.currency} {po.total_po_value?.toLocaleString()}</td>
                   <td>{po.currency}</td>
                   <td>{statusBadge(po.po_status)}</td>
                   <td>{po.requisition_number || '—'}</td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && <CreatePOModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadData(); }} />}
    </div>
  );
}

function CreatePOModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ supplier_id: '', delivery_port: '', required_by_date: '', currency: 'USD', remarks: '', items: [{ description: '', qty: 1, unit_price: 0 }] });
  const [suppliers, setSuppliers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get('/purchasing/suppliers').then(r => setSuppliers(r.data)).catch(console.error); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/purchasing/purchase-orders', form); onCreated(); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  const total = form.items.reduce((sum, i) => sum + (i.qty * i.unit_price), 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Create Purchase Order</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group"><label className="form-label">Supplier</label>
                <select className="form-select" value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })} required>
                  <option value="">Select supplier...</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.supplier_name} ({s.country})</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Delivery Port</label><input className="form-input" value={form.delivery_port} onChange={e => setForm({ ...form, delivery_port: e.target.value })} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Required By</label><input className="form-input" type="date" value={form.required_by_date} onChange={e => setForm({ ...form, required_by_date: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Currency</label><select className="form-select" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                {['USD','EUR','SGD','GBP'].map(c => <option key={c}>{c}</option>)}
              </select></div>
            </div>
            <div className="form-group">
              <label className="form-label">Line Items</label>
              {form.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input className="form-input" placeholder="Description" value={item.description} onChange={e => { const items = [...form.items]; items[i] = { ...items[i], description: e.target.value }; setForm({ ...form, items }); }} style={{ flex: 3 }} />
                  <input className="form-input" type="number" placeholder="Qty" value={item.qty} onChange={e => { const items = [...form.items]; items[i] = { ...items[i], qty: parseInt(e.target.value) }; setForm({ ...form, items }); }} style={{ flex: 1 }} />
                  <input className="form-input" type="number" placeholder="Unit Price" value={item.unit_price} onChange={e => { const items = [...form.items]; items[i] = { ...items[i], unit_price: parseFloat(e.target.value) }; setForm({ ...form, items }); }} style={{ flex: 1 }} />
                </div>
              ))}
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setForm({ ...form, items: [...form.items, { description: '', qty: 1, unit_price: 0 }] })}>+ Add Item</button>
              <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 600 }}>Total: {form.currency} {total.toLocaleString()}</div>
            </div>
            <div className="form-group"><label className="form-label">Remarks</label><textarea className="form-textarea" value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create PO'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Suppliers
function SuppliersTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { api.get('/purchasing/suppliers').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false)); }, []);

  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary text-sm">Manage approved suppliers and vendors.</p>
        <button className="btn btn-accent btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Add Supplier</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         <div className="table-container">
           <table>
             <thead><tr><th>Supplier</th><th>Country</th><th>Categories</th><th>Lead Time</th><th>Reliability</th><th>Preferred</th></tr></thead>
             <tbody>
               {data.map(s => (
                 <tr key={s.id}>
                   <td><strong>{s.supplier_name}</strong></td>
                   <td>{s.country}</td>
                   <td style={{ fontSize: 12 }}>{s.parts_categories}</td>
                   <td>{s.lead_time_days} days</td>
                   <td>
                     <div className="health-bar" style={{ maxWidth: 80 }}>
                       <div className="health-bar-inner"><div className="health-bar-fill healthy" style={{ width: `${s.reliability_score}%` }}></div></div>
                       <span className="health-score">{Math.round(s.reliability_score)}</span>
                     </div>
                   </td>
                   <td>{s.is_preferred ? <span className="badge badge-healthy">Preferred</span> : '—'}</td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && <CreateSupplierModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); api.get('/purchasing/suppliers').then(r => setData(r.data)); }} />}
    </div>
  );
}

function CreateSupplierModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ supplier_name: '', country: '', email: '', phone: '', parts_categories: '', lead_time_days: '', is_preferred: false });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/purchasing/suppliers', form); onCreated(); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Add Supplier</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Supplier Name *</label><input className="form-input" value={form.supplier_name} onChange={e => setForm({ ...form, supplier_name: e.target.value })} required /></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Country</label><input className="form-input" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Lead Time (days)</label><input className="form-input" type="number" value={form.lead_time_days} onChange={e => setForm({ ...form, lead_time_days: e.target.value })} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div className="form-group"><label className="form-label">Parts Categories</label><input className="form-input" value={form.parts_categories} onChange={e => setForm({ ...form, parts_categories: e.target.value })} placeholder="e.g. Engine Parts, Safety Equipment" /></div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={form.is_preferred} onChange={e => setForm({ ...form, is_preferred: e.target.checked })} /> Preferred Supplier</label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>Add Supplier</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Goods Receipts
function GoodsReceiptsTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { api.get('/purchasing/goods-receipts').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false)); }, []);

  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary text-sm">Record parts received onboard against purchase orders.</p>
        <button className="btn btn-accent btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Record Receipt</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         data.length === 0 ? <div className="empty-state"><Truck /><h4>No goods receipts recorded</h4></div> :
         <div className="table-container">
           <table><thead><tr><th>PO #</th><th>Supplier</th><th>Receipt Date</th><th>Received By</th><th>Delivery Note</th><th>Items</th><th>Location</th></tr></thead>
             <tbody>{data.map(r => <tr key={r.id}><td><strong>{r.po_number}</strong></td><td>{r.supplier_name}</td><td>{r.receipt_date}</td><td>{r.received_by}</td><td>{r.delivery_note_number}</td><td>{r.items_received?.length || 0} item(s)</td><td>{r.location_stored}</td></tr>)}</tbody>
           </table>
         </div>
        }
      </div>
    </div>
  );
}

// Requisition Status Tracker
function TrackerTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get('/purchasing/requisition-tracker').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false)); }, []);

  return (
    <div className="card">
      {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
       <div className="table-container">
         <table>
           <thead><tr><th>Req #</th><th>Type</th><th>Required By</th><th>Status</th><th>PO #</th><th>PO Status</th><th>Overdue</th></tr></thead>
           <tbody>
             {data.map(r => (
               <tr key={r.id}>
                 <td><strong>{r.requisition_number}</strong></td>
                 <td><span className={`badge ${r.requisition_type === 'AI-Generated' ? 'badge-ai' : 'badge-draft'}`}>{r.requisition_type}</span></td>
                 <td>{r.required_by_date}</td>
                 <td><span className={`badge badge-${r.status.toLowerCase()}`}>{r.status}</span></td>
                 <td>{r.po_number || '—'}</td>
                 <td>{r.po_status ? <span className={`badge ${r.po_status === 'Delivered' ? 'badge-closed' : 'badge-info'}`}>{r.po_status}</span> : '—'}</td>
                 <td>{r.is_overdue ? <span className="badge badge-critical">OVERDUE</span> : '—'}</td>
               </tr>
             ))}
           </tbody>
         </table>
       </div>
      }
    </div>
  );
}
